/**
 * File: ConversionSectionModal.tsx
 * Description: Full-screen modal to view and edit a single converted
 *              SAHLD section. Renders markdown (including Mermaid diagrams)
 *              using MonacoMarkdownEditor and persists edits via the
 *              brownfield conversion PATCH endpoint.
 * Author: GitHub Copilot
 * Created: 2026-03-04
 * Last Modified: 2026-03-04
 */

import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';

import { MonacoMarkdownEditor } from '../../components/monaco/MonacoMarkdownEditor';
import { updateSection } from '../../services/brownfieldConversionService';

// ---- Types -----------------------------------------------------------------

export interface ConversionSectionModalProps {
  /** Numeric project ID used for the PATCH save call. */
  projectId: number;
  /** Machine key for this section (e.g. ``adr_summary``). */
  sectionName: string;
  /** Human-readable label shown in the modal header. */
  sectionLabel: string;
  /** Emoji icon shown next to the label. */
  sectionIcon: string;
  /** Current markdown content for the section. */
  content: string;
  /** Called when the modal is dismissed (no change). */
  onClose: () => void;
  /**
   * Called after a successful save so the parent can update its state.
   *
   * @param sectionName - The section that was saved.
   * @param newContent - The updated markdown content.
   */
  onSaved: (_sectionName: string, _newContent: string) => void;
}

// ---- Component -------------------------------------------------------------

/**
 * ConversionSectionModal — overlays the full viewport with a markdown editor
 * pre-loaded with the given section content. Mermaid diagrams are rendered
 * inline via the split / preview modes in MonacoMarkdownEditor. Edits are
 * persisted to the backend on Save.
 */
export const ConversionSectionModal: React.FC<
  ConversionSectionModalProps
> = ({
  projectId,
  sectionName,
  sectionLabel,
  sectionIcon,
  content,
  onClose,
  onSaved,
}) => {
  const [draftContent, setDraftContent] = useState(content);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Sync if the parent ever passes fresh content (e.g. after a re-run).
  useEffect(() => {
    setDraftContent(content);
    setIsDirty(false);
    setSaveError(null);
  }, [content]);

  // Escape key closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleChange = useCallback((value: string) => {
    setDraftContent(value);
    setIsDirty(true);
    setSaveError(null);
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateSection(projectId, sectionName, draftContent);
      setIsDirty(false);
      onSaved(sectionName, draftContent);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Save failed. Please retry.',
      );
    } finally {
      setIsSaving(false);
    }
  }, [projectId, sectionName, draftContent, onSaved]);

  const isFormReady = !isSaving && isDirty;
  const isSaveDisabled = !isFormReady;

  return (
    /* Backdrop */
    <dialog
      open
      className="fixed inset-0 z-50 flex items-stretch
                 justify-center p-0 border-0"
      aria-modal="true"
      aria-label={`Edit ${sectionLabel}`}
    >
      {/* Backdrop overlay — click outside to close */}
      <button
        type="button"
        className="fixed inset-0 bg-black/50 backdrop-blur-sm cursor-default"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') { onClose(); } }}
        aria-label="Close modal"
        tabIndex={-1}
      />
      {/* Modal panel */}
      <div
        className="relative flex flex-col w-full max-w-7xl mx-4 my-4
                   z-10 bg-white rounded-xl shadow-2xl overflow-hidden"
      >
        {/* ---- Header ---- */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 bg-white shrink-0">
          <span className="text-2xl" aria-hidden>
            {sectionIcon}
          </span>
          <h2 className="flex-1 text-base font-semibold text-gray-900 truncate">
            {sectionLabel}
          </h2>

          {/* Save error */}
          {saveError && (
            <span className="text-xs text-red-600 mr-2 max-w-xs truncate">
              {saveError}
            </span>
          )}

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className={
              isSaveDisabled
                ? 'px-4 py-1.5 rounded text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'px-4 py-1.5 rounded text-sm font-medium bg-[#0097a9] text-white hover:bg-[#00798a] transition'
            }
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            className="ml-1 p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* ---- Editor (fills remaining height) ---- */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <MonacoMarkdownEditor
            value={draftContent}
            onChange={handleChange}
            height="calc(100dvh - 175px)"
            theme="light"
            isEditable={true}
            isEditing={true}
            defaultViewMode="split"
            iconOnlyButtons={true}
            onSave={handleSave}
            isSaving={isSaving}
            section={sectionName}
          />
        </div>
      </div>
    </dialog>
  );
};

export default ConversionSectionModal;
